import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as errorCodes from '../common/errorCodes';
import * as JspUtil from '../common/JspUtil';
import * as actions from './actionCreators';
import PagePasswordRules from './../passwordpages/PagePasswordRules';
import PasswordMeter from './../passwordpages/PasswordMeter';
import { useJspVariables, useAppContext, useAuth, useLoader, useDebugRender, usePageTitle } from '../hooks';
import { IceIcon, PageContainer, PageHeader, PasswordInput, TextInput, DialogTitleWithClose } from '../components';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

const PageConfirmUserEmail = () => {
    useDebugRender('PageConfirmUserEmail');
    usePageTitle('Confirm Email');
    const navigate = useNavigate();
    const { panelStyle, launcher, jspVariables } = useJspVariables();
    const { getLabelFromJspVariables, onUserNameChange, onAppNameChange } = useAppContext(jspVariables);
    const { showLoader, hideLoader } = useLoader();

    const { authenticatedUser } = useAuth();
    // This page is reached from an email link (create-password flow) where the
    // user is NOT authenticated, so useAuth() is empty. The server provides the
    // user id via jspUserId — prefer it, falling back to the authenticated user
    // for any authenticated re-entry. Without this the User ID field is blank.
    const userName = jspVariables.jspUserId || authenticatedUser?.authenticatedUser || '';

    const [newPassword, setNewPassword] = useState('');
    const [newConfirmPassword, setNewConfirmPassword] = useState('');
    const [alert, setAlert] = useState(null);
    const [successDialog, setSuccessDialog] = useState(null);
    const [errors, setErrors] = useState({});

    const validate = () => {
        const fieldErrors = {};
        if (!newPassword) fieldErrors.newPassword = errorCodes.MSG_PASSWORD_REQUIRED();
        if (!newConfirmPassword) fieldErrors.newConfirmPassword = errorCodes.MSG_CONFIRM_PASSWORD_REQUIRED();
        if (newPassword && newConfirmPassword && newPassword !== newConfirmPassword) {
            fieldErrors.newConfirmPassword = errorCodes.MSG_PASSWORDS_DO_NOT_MATCH();
        }
        setErrors(fieldErrors);
        return Object.keys(fieldErrors).length === 0;
    };

    const clearFieldError = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    useEffect(() => {
        if (JspUtil.isValueAvailable(jspVariables.jspUserId))
            onUserNameChange(jspVariables.jspUserId);
        if (JspUtil.isValueAvailable(jspVariables.jspApp)) {
            if (jspVariables.jspApp != 'ANY') {
                onAppNameChange(jspVariables.jspApp);
            }
        }
    }, []);

    const onFormSubmit = async (e) => {
        e.preventDefault();
        setAlert(null);
        if (!validate()) return;
        showLoader('Confirming email...');
        try {
            await actions.confirmUserEmail(jspVariables.jspUserId, jspVariables.jspApp, jspVariables.jspRedirectUrl,
                newPassword, jspVariables.jspOtpCode);
            setSuccessDialog({
                heading: getLabelFromJspVariables('title_confirm_user_email'),
                message: JspUtil.getLabelFromJspVariables('confirm_user_email_success_msg', jspVariables.jspUserId),
                redirectUrl: jspVariables.jspRedirectUrl
            });
        } catch (resp) {
            setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
        } finally {
            hideLoader();
        }
    };

    const backToApplicationEnabled = jspVariables.jspBackToApplicationEnabled !== 'N';

    return (
        <PageContainer id="pageConfirmUserEmail" dataAutomationId="page_confirm_user_email">
            <MuiDialog open={!!successDialog} maxWidth="sm" fullWidth
                aria-labelledby="confirm-user-email-success-title"
                aria-describedby="confirm-user-email-success-description">
                <DialogTitleWithClose id="confirm-user-email-success-title" severity="success" onClose={() => navigate(constants.ROUTE_PAGE_LOGIN)} dataAutomationId="confirm_user_email_success_title">
                    {successDialog?.heading}
                </DialogTitleWithClose>
                <DialogContent>
                    <DialogContentText id="confirm-user-email-success-description">{successDialog?.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    {successDialog?.redirectUrl && backToApplicationEnabled &&
                        <Button variant="outlined"
                            onClick={() => { window.location.href = successDialog.redirectUrl; }}
                            data-automation-id="confirm_user_email_back_to_app_btn">
                            {getLabelFromJspVariables('action_return_to_application')}
                        </Button>
                    }
                    <Button variant="contained"
                        onClick={() => navigate(constants.ROUTE_PAGE_LOGIN)}
                        data-automation-id="confirm_user_email_success_login_btn">
                        {getLabelFromJspVariables('action_login')}
                    </Button>
                </DialogActions>
            </MuiDialog>
            <Box sx={panelStyle}>
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="confirm_user_email_header">{getLabelFromJspVariables('title_confirm_user_email')}</PageHeader>
                {alert && <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                <Box component="form" onSubmit={onFormSubmit} id="formConfirmUserEmail" data-automation-id="form_confirm_user_email">
                    <Alert severity="info" id="gid_registration_intro" data-automation-id="gid_registration_intro" sx={{ mb: 1 }}>
                        {getLabelFromJspVariables('confirm_user_email_info_msg')}
                    </Alert>
                    <Box sx={{ mt: 1 }}>
                        <TextInput
                            label={getLabelFromJspVariables('label_userid')}
                            id='username_input'
                            dataAutomationId='username_input'
                            value={userName}
                            disabled={true}
                        />
                    </Box>
                    <Box>
                        <PasswordInput
                            label={getLabelFromJspVariables('label_password')}
                            autoComplete='off'
                            value={newPassword}
                            id='password_input'
                            dataAutomationId='new_password_input'
                            required
                            error={!!errors.newPassword}
                            helperText={errors.newPassword || ''}
                            onChange={e => { setNewPassword(e.target.value); clearFieldError('newPassword'); }}
                        />
                        <PasswordMeter password={newPassword} />
                    </Box>
                    <PasswordInput
                        label={getLabelFromJspVariables('action_confirm_password')}
                        id='password_input1'
                        dataAutomationId='confirm_password_input'
                        autoComplete='off'
                        value={newConfirmPassword}
                        required
                        error={!!errors.newConfirmPassword}
                        helperText={errors.newConfirmPassword || ''}
                        onChange={e => { setNewConfirmPassword(e.target.value); clearFieldError('newConfirmPassword'); }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button variant="contained" fullWidth type="submit"
                            data-automation-id="confirm_user_email_done_btn">
                            {getLabelFromJspVariables('action_save')}
                        </Button>
                    </Box>
                    <PagePasswordRules />
                </Box>
            </Box>
        </PageContainer>
    );
};

export default PageConfirmUserEmail;
